/**
 * Bemérés helyszín: GPS-first Maps linkek + reverse-geocode minőség.
 * Nominatim gyakran csak települést ad SK/HU falvakban; a koordináta a megbízható forrás.
 */

export type ReverseGeocodeResult = {
  displayName: string;
  precise: boolean;
  settlement: string | null;
  source: "nominatim" | "google" | "coords";
};

/** Van-e házszám a szöveges címben (ne irányítószámot nézzünk házszámnak). */
export function isPreciseMeasurementAddress(address: string | null | undefined): boolean {
  const a = (address || "").trim();
  if (!a) return false;
  if (/^-?\d+\.\d+,\s*-?\d+\.\d+$/.test(a)) return false;
  if (/·\s*GPS\s+-?\d+\.\d+/i.test(a)) return false;
  // Nominatim: "123/4, Street, Town..." vagy "123, Street..."
  if (/^\d{1,5}(\/\d+[A-Za-z]?)?\b/.test(a)) return true;
  // Bármely "123/4" stílusú házszám a stringben
  if (/\b\d{1,5}\/\d+[A-Za-z]?\b/.test(a)) return true;
  return false;
}

export function formatGpsPair(lat: number, lng: number): string {
  return `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
}

/** Deal GPS mindkét casingből (API normalize előtt/után). */
export function resolveDealGeo(deal: {
  geoLat?: number | null;
  geoLng?: number | null;
  geo_lat?: number | null;
  geo_lng?: number | null;
} | null | undefined): { lat: number; lng: number } | null {
  if (!deal) return null;
  const latRaw = deal.geoLat ?? deal.geo_lat;
  const lngRaw = deal.geoLng ?? deal.geo_lng;
  const lat = latRaw != null ? Number(latRaw) : NaN;
  const lng = lngRaw != null ? Number(lngRaw) : NaN;
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { lat, lng };
}

/**
 * Google Maps URL: ha van GPS, mindig a koordinátára mutasson
 * (a városnév-search a városközpontot nyitná).
 */
export function buildMeasurementMapsUrl(opts: {
  geoLat?: number | null;
  geoLng?: number | null;
  geo_lat?: number | null;
  geo_lng?: number | null;
  address?: string | null;
}): string | null {
  const geo = resolveDealGeo(opts);
  if (geo) {
    return `https://www.google.com/maps?q=${geo.lat},${geo.lng}`;
  }
  const address = (opts.address || "").trim();
  if (!address) return null;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
}

function settlementFromNominatimAddress(
  addr: Record<string, string> | null | undefined,
): string | null {
  if (!addr) return null;
  return (
    addr.village ||
    addr.town ||
    addr.city ||
    addr.municipality ||
    addr.hamlet ||
    addr.suburb ||
    null
  );
}

export function parseNominatimReverse(json: any): ReverseGeocodeResult | null {
  const displayName = typeof json?.display_name === "string" ? json.display_name.trim() : "";
  if (!displayName) return null;
  const addr = (json?.address || {}) as Record<string, string>;
  // Utca házszám nélkül még mindig túl durva kapu/bemérés helyhez.
  const hasHouse = Boolean(addr.house_number && String(addr.house_number).trim());
  return {
    displayName,
    precise: hasHouse,
    settlement: settlementFromNominatimAddress(addr),
    source: "nominatim",
  };
}

export function parseGoogleReverse(json: any): ReverseGeocodeResult | null {
  const results = Array.isArray(json?.results) ? json.results : [];
  if (!results.length) return null;

  const pick =
    results.find((r: any) => (r.types || []).includes("street_address")) ||
    results.find((r: any) => (r.types || []).includes("premise")) ||
    results.find((r: any) => (r.types || []).includes("route")) ||
    results[0];

  const displayName = typeof pick?.formatted_address === "string" ? pick.formatted_address.trim() : "";
  if (!displayName) return null;

  const components: Array<{ long_name: string; short_name: string; types: string[] }> =
    pick.address_components || [];
  const has = (type: string) =>
    components.some((c) => (c.types || []).includes(type) && (c.long_name || "").trim());
  const get = (type: string) =>
    components.find((c) => (c.types || []).includes(type))?.long_name?.trim() || null;

  return {
    displayName,
    precise: has("street_number"),
    settlement:
      get("locality") ||
      get("postal_town") ||
      get("sublocality") ||
      get("administrative_area_level_2"),
    source: "google",
  };
}

/**
 * Mentendő cím: precíz reverse → display_name;
 * durva → település + GPS (visszakereshető); reverse nélkül → GPS.
 */
export function buildMeasurementAddress(
  reverse: ReverseGeocodeResult | null,
  lat: number,
  lng: number,
): { address: string; precise: boolean } {
  const gps = formatGpsPair(lat, lng);
  if (!reverse) {
    return { address: gps, precise: false };
  }
  if (reverse.precise) {
    return { address: reverse.displayName, precise: true };
  }
  const label = (reverse.settlement || reverse.displayName.split(",")[0] || "").trim();
  const address = label ? `${label} · GPS ${gps}` : `GPS ${gps}`;
  return { address, precise: false };
}

export async function reverseGeocodeNominatim(
  lat: number,
  lng: number,
  fetchImpl: typeof fetch = fetch,
): Promise<ReverseGeocodeResult | null> {
  try {
    const url =
      `https://nominatim.openstreetmap.org/reverse?format=jsonv2` +
      `&lat=${encodeURIComponent(lat)}&lon=${encodeURIComponent(lng)}` +
      `&zoom=18&addressdetails=1`;
    const r = await fetchImpl(url, {
      headers: { Accept: "application/json" },
    });
    if (!r.ok) return null;
    return parseNominatimReverse(await r.json());
  } catch {
    return null;
  }
}

export async function reverseGeocodeGoogle(
  lat: number,
  lng: number,
  apiKey: string,
  fetchImpl: typeof fetch = fetch,
): Promise<ReverseGeocodeResult | null> {
  if (!apiKey) return null;
  try {
    const url =
      `https://maps.googleapis.com/maps/api/geocode/json` +
      `?latlng=${encodeURIComponent(lat)},${encodeURIComponent(lng)}` +
      `&language=sk&key=${encodeURIComponent(apiKey)}`;
    const r = await fetchImpl(url);
    if (!r.ok) return null;
    const json = await r.json();
    if (json?.status && json.status !== "OK" && json.status !== "ZERO_RESULTS") {
      return null;
    }
    return parseGoogleReverse(json);
  } catch {
    return null;
  }
}

/** Google (ha van kulcs) → Nominatim → null. Preferáld a precíz találatot. */
export async function reverseGeocodeBest(
  lat: number,
  lng: number,
  opts?: { googleApiKey?: string | null; fetchImpl?: typeof fetch },
): Promise<ReverseGeocodeResult | null> {
  const fetchImpl = opts?.fetchImpl ?? fetch;
  const key = opts?.googleApiKey || null;

  let google: ReverseGeocodeResult | null = null;
  if (key) {
    google = await reverseGeocodeGoogle(lat, lng, key, fetchImpl);
    if (google?.precise) return google;
  }

  const nominatim = await reverseGeocodeNominatim(lat, lng, fetchImpl);
  if (nominatim?.precise) return nominatim;
  // Ha egyik sem precíz, a Google display gyakran jobb (formázott cím)
  if (google) return google;
  return nominatim;
}
